import React, { useRef, useEffect, useState, forwardRef, useImperativeHandle } from "react";

/**
 * Lightweight signature pad — pure HTML5 canvas, no dependency.
 *
 * Exposes via ref: clear(), toDataURL(), isEmpty().
 */
const SignaturePad = forwardRef(function SignaturePad(
  { width = 480, height = 140, color = "#0B1F4D", testid },
  ref,
) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastRef = useRef(null);
  const dirtyRef = useRef(false);
  const [empty, setEmpty] = useState(true);

  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    // High-DPI rendering
    const ratio = window.devicePixelRatio || 1;
    cv.width = width * ratio;
    cv.height = height * ratio;
    cv.style.width = `${width}px`;
    cv.style.height = `${height}px`;
    const ctx = cv.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = color;
    ctx.lineWidth = 2.2;
  }, [width, height, color]);

  const pos = (ev) => {
    const cv = canvasRef.current;
    const rect = cv.getBoundingClientRect();
    const point = ev.touches ? ev.touches[0] : ev;
    return { x: point.clientX - rect.left, y: point.clientY - rect.top };
  };

  const start = (ev) => {
    ev.preventDefault();
    drawingRef.current = true;
    lastRef.current = pos(ev);
  };
  const move = (ev) => {
    if (!drawingRef.current) return;
    ev.preventDefault();
    const cur = pos(ev);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(cur.x, cur.y);
    ctx.stroke();
    lastRef.current = cur;
    if (!dirtyRef.current) {
      dirtyRef.current = true;
      setEmpty(false);
    }
  };
  const end = () => { drawingRef.current = false; };

  useImperativeHandle(ref, () => ({
    clear: () => {
      const ctx = canvasRef.current.getContext("2d");
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      dirtyRef.current = false;
      setEmpty(true);
    },
    toDataURL: () => canvasRef.current.toDataURL("image/png"),
    isEmpty: () => !dirtyRef.current,
  }), []);

  return (
    <div className="relative bg-white border border-navy/20 rounded-sm overflow-hidden">
      <canvas
        ref={canvasRef}
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        className="block touch-none cursor-crosshair w-full"
        data-testid={testid}
      />
      {empty && (
        <p className="absolute inset-0 flex items-center justify-center pointer-events-none text-xs text-slate-400 italic">
          Sign here with mouse or finger
        </p>
      )}
    </div>
  );
});

export default SignaturePad;
